import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Container,
  Paper,
  Stack,
  TextField,
  Typography,
} from '@mui/material';

import api from '../services/api';

type ProformaPayload = {
  proforma_number: string;
  status: string;
  company_name?: string | null;
  contact_name?: string | null;
  contact_email?: string | null;
  contact_phone?: string | null;
  concrete_type?: string;
  final_quantity?: number | string;
  total_amount?: number | string;
  delivery_address?: string | null;
  valid_until?: string;
  public_validation_expires_at?: string | null;
};

type ValidationResponse = {
  proforma: ProformaPayload;
  validation_status: string;
  is_expired: boolean;
  validation_expires_at?: string | null;
};

const formatAmount = (value?: number | string) => {
  const amount = Number(value || 0);
  return new Intl.NumberFormat('fr-FR').format(amount);
};

const ProformaValidation: React.FC = () => {
  const { token } = useParams<{ token: string }>();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [payload, setPayload] = useState<ValidationResponse | null>(null);
  const [requestedDeliveryDate, setRequestedDeliveryDate] = useState('');
  const [deliveryAddress, setDeliveryAddress] = useState('');
  const [contactName, setContactName] = useState('');
  const [contactPhone, setContactPhone] = useState('');

  const validationDateLabel = useMemo(() => {
    if (!payload?.validation_expires_at) return '';
    return new Date(payload.validation_expires_at).toLocaleString('fr-FR');
  }, [payload]);

  useEffect(() => {
    const fetchProforma = async () => {
      if (!token) {
        setError('Lien de validation invalide.');
        setLoading(false);
        return;
      }

      try {
        const response = await api.get<ValidationResponse>(`/orders/public/proformas/validate/${token}/`);
        setPayload(response.data);

        const proforma = response.data.proforma;
        setDeliveryAddress(proforma.delivery_address || '');
        setContactName(proforma.contact_name || '');
        setContactPhone(proforma.contact_phone || '');

        const tomorrow = new Date();
        tomorrow.setDate(tomorrow.getDate() + 1);
        setRequestedDeliveryDate(tomorrow.toISOString().slice(0, 10));
      } catch (err: any) {
        const status = err?.response?.status;
        if (status === 410) {
          setError('Ce lien de validation a expiré.');
        } else if (status === 404) {
          setError('Proforma introuvable.');
        } else {
          setError('Impossible de charger la proforma.');
        }
      } finally {
        setLoading(false);
      }
    };

    fetchProforma();
  }, [token]);

  const handleAccept = async () => {
    if (!token) return;
    setSubmitting(true);
    setError('');
    try {
      const response = await api.post(`/orders/public/proformas/validate/${token}/`, {
        action: 'accept',
        requested_delivery_date: requestedDeliveryDate,
        delivery_address: deliveryAddress,
        delivery_contact_name: contactName,
        delivery_contact_phone: contactPhone,
      });

      setSuccess(`Commande validée avec succès. Numéro: ${response.data?.order?.order_number || 'N/A'}`);
    } catch (err: any) {
      setError(err?.response?.data?.error || 'Impossible de valider la commande.');
    } finally {
      setSubmitting(false);
    }
  };

  const handleReject = async () => {
    if (!token) return;
    const confirmed = window.confirm('Confirmer le rejet de cette proforma ?');
    if (!confirmed) return;

    setSubmitting(true);
    setError('');
    try {
      await api.post(`/orders/public/proformas/validate/${token}/`, {
        action: 'reject',
      });
      setSuccess('La proforma a été rejetée.');
    } catch (err: any) {
      setError(err?.response?.data?.error || 'Impossible de rejeter la proforma.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Box sx={{ minHeight: '100vh', background: 'linear-gradient(180deg, #eef5fa 0%, #ffffff 100%)', pt: 12, pb: 8 }}>
      <Container maxWidth="md">
        <Paper elevation={0} sx={{ p: { xs: 3, md: 5 }, border: '1px solid rgba(1,78,154,0.12)' }}>
          {loading ? (
            <Stack alignItems="center" spacing={2} sx={{ py: 8 }}>
              <CircularProgress sx={{ color: '#014E9A' }} />
              <Typography color="text.secondary">Chargement du devis...</Typography>
            </Stack>
          ) : error ? (
            <Alert severity="error">{error}</Alert>
          ) : success ? (
            <Stack spacing={2}>
              <Alert severity="success">{success}</Alert>
              <Button variant="contained" onClick={() => navigate('/')}>
                Retour à l'accueil
              </Button>
            </Stack>
          ) : payload ? (
            <Stack spacing={4}>
              <Box>
                <Typography variant="overline" sx={{ color: '#014E9A', fontWeight: 700 }}>
                  Validation du proforma
                </Typography>
                <Typography variant="h4" sx={{ fontWeight: 700, mt: 1 }}>
                  {payload.proforma.proforma_number}
                </Typography>
                <Typography color="text.secondary" sx={{ mt: 1 }}>
                  Vérifiez les informations ci-dessous puis validez la commande.
                </Typography>
              </Box>

              <Stack spacing={2}>
                <Box sx={{ display: 'grid', gap: 2, gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' } }}>
                  <TextField label="Entreprise" value={payload.proforma.company_name || ''} disabled fullWidth />
                  <TextField label="Contact" value={contactName} onChange={(e) => setContactName(e.target.value)} fullWidth />
                  <TextField label="Téléphone" value={contactPhone} onChange={(e) => setContactPhone(e.target.value)} fullWidth />
                  <TextField label="Type de béton" value={payload.proforma.concrete_type || ''} disabled fullWidth />
                </Box>

                <TextField
                  label="Adresse de livraison"
                  value={deliveryAddress}
                  onChange={(e) => setDeliveryAddress(e.target.value)}
                  fullWidth
                  multiline
                  minRows={3}
                />

                <Box sx={{ display: 'grid', gap: 2, gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' } }}>
                  <TextField
                    label="Quantité"
                    value={`${payload.proforma.final_quantity || 0} m³`}
                    disabled
                    fullWidth
                  />
                  <TextField
                    label="Montant total"
                    value={`${formatAmount(payload.proforma.total_amount)} FCFA`}
                    disabled
                    fullWidth
                  />
                  <TextField
                    type="date"
                    label="Date de livraison souhaitée"
                    value={requestedDeliveryDate}
                    onChange={(e) => setRequestedDeliveryDate(e.target.value)}
                    InputLabelProps={{ shrink: true }}
                    fullWidth
                  />
                  <TextField
                    label="Lien valable jusqu'au"
                    value={validationDateLabel}
                    disabled
                    fullWidth
                  />
                </Box>
              </Stack>

              <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
                <Button
                  variant="contained"
                  size="large"
                  onClick={handleAccept}
                  disabled={submitting || !requestedDeliveryDate}
                  sx={{ bgcolor: '#014E9A', '&:hover': { bgcolor: '#013F7E' } }}
                >
                  {submitting ? 'Validation...' : 'Valider ma commande'}
                </Button>
                <Button
                  variant="outlined"
                  size="large"
                  onClick={handleReject}
                  disabled={submitting}
                  sx={{ borderColor: '#014E9A', color: '#014E9A' }}
                >
                  Rejeter
                </Button>
              </Stack>
            </Stack>
          ) : null}
        </Paper>
      </Container>
    </Box>
  );
};

export default ProformaValidation;
